import React from 'react';
import FullWidthBarBreadcrumbs from '@/components/Breadcrumb/FullWidthBarBreadcrumbs';
import CenteredContentSection from '@/components/ContentSection/CenteredContentSection';
import { H3, H4 } from '@/components/Element/Heading';
import MetaTags from '@/components/Head/MetaTags';
// import SimpleCenteredHeaderSection from '@/components/HeaderSection/SimpleCenteredHeaderSection';
import SideCardContent from '@/components/SideCardContent/SideCardContent';
import SideCardCTA from '@/components/SideCardCTA/SideCardCTA2';
import FullWidthFormSection from '@/components/LeadForm/FullWidthFormSection';
import Link from 'next/link';
import FullWidthGuideFormSection from '@/components/LeadForm/FullWidthGuideForm';
import Image1 from 'images/solar-guide/image52.png';
import Image2 from 'images/solar-guide/image65.png';
import Image3 from 'images/solar-guide/image8.png';
import Image4 from 'images/solar-guide/image18.png';
import Image from 'next/legacy/image';
import ReadingTime from '@/components/ReadingTime/ReadingTime';
import { SolarGuideTable } from './why-go-solar';

const Page = () => {
  return (
    <article>
      <MetaTags
        title="Your Steps to Install a Home Solar System"
        description="2023 Home Solar Guide"
      />
      <FullWidthBarBreadcrumbs
        pages={[
          {
            name: 'Home Solar Guide',
            href: '/home-solar-guide',
          },
          {
            name: '⏭️ Your Steps to Install a Home Solar System',
            href: '/home-solar-guide/steps-to-install-a-home-solar-system',
          },
        ]}
      />
      <CenteredContentSection>
        <section id="section1">
          <div className="flex justify-between items-center">
            <Link href={'/home-solar-guide/battery-storage-products'}>
              ← Battery Storage Products
            </Link>
            <Link href={'/home-solar-guide/economics-of-home-solar'}>
              Economics of Home Solar →
            </Link>
          </div>
          <H3>⏭️ Your Steps to Install a Home Solar System</H3>
          <div className="mt-[-30px]">
            <ReadingTime wordCount={1723} />
          </div>
          <p>
            In this section we discuss the typical steps homeowners like you
            take to get a solar system installed at their home. This process
            begins with an inquiry and ends with a newly installed solar system
            being activated.
          </p>
          <Image src={Image1} alt="Steps to install a home solar system." />
          <H4>Qualification</H4>
          <p>
            The first real step in your journey is to inquire with one or more
            companies about getting solar for your home. You may fill out web
            forms after doing some Google searching, see a flier in your mailbox
            or get a reference from a friend or neighbor and contact a solar
            sales representative directly.
          </p>
          <p>
            <strong>These questions may include:</strong>{' '}
          </p>
          <ul className=" ml-6">
            <li className="mb-2">Do you own your home?</li>
            <li className="mb-2">
              What amount of energy have you used in the past year?
            </li>
            <li className="mb-2">What is your roof made of?</li>
            <li className="mb-2">How old is your roof?</li>
          </ul>

          <p>
            The solar company will also look at satellite maps to determine
            where the most exposure to sunlight is for panel placement. In some
            cases, you’re going to be asked if you’re willing to cut trees. To
            you the qualification process will feel more like a conversation of
            question and answer for maybe 5 to 10 minutes.
          </p>
          <Image src={Image2} alt="Satalite of a house" />
          <H4>Design the System</H4>
          <p>
            Once you are qualified by EnergyPal or another solar company you
            will typically receive an initial design of your solar system within
            the proposal or price quote. The system design will often be
            calibrated to include enough solar panels to produce a sufficient
            amount of energy over the course of a year to offset the majority of
            your home&apos;s current or expected energy usage. The system design
            will usually be either a satellite image of the home, or it will be
            a schematic of the home, with solar panels laid out on top. When
            reviewing the design you may have some questions or concerns about
            where panels are placed.
          </p>
          <p>
            Sometimes solar companies will reduce the size of a system to show a
            total lower price even if it doesn&apos;t cover the full utility
            bill. This is often referred to as “under-sizing”. Sometimes solar
            companies may oversize the system simply to maximize their total
            sales price. It&apos;s important to be aware of local utility
            regulations as they may provide constraints or rules on the size or
            total production potential of your system.
          </p>
          <H4>Compare Quotes</H4>
          <p>
            With quotes and proposals and system designs in hand this is when
            you will usually begin a process of comparing the quotes and trying
            to understand how they suit your needs. You may be interested in the
            lowest monthly payment or you may be interested in the highest total
            power produced or they may get stuck on particular aspects such as
            the system size in kilowatts (kW), the type of inverter or the
            location of the panels on the roof. There&apos;s a lot of
            information provided in solar proposals that take some time to
            digest and absorb if you&apos;re doing it on your own.
          </p>
          <p>
            The bad news for you is that comparing quotes is extremely
            difficult. The data is presented in different forms and is not
            always apples-to-apples. Companies may purposely present information
            in a biased way to try to win a customer by portraying the
            information in a very favorable light.
          </p>
          <p>
            The good news for you is that we take this pain in the buying
            process as a huge part of our value proposition. We can clearly
            educate why some quotes are better and why some quotes are worse for
            your specific situation.
          </p>
          <p>
            <strong>Let us help you compare quotes:</strong>
          </p>
        </section>
      </CenteredContentSection>
      <FullWidthFormSection cid="a4oqmxxt5un31ha0" />
      <CenteredContentSection>
        <p>
          EnergyPal has access to many top-rated solar installers with high
          quality panels and great financing options. We make it easy to assist
          homeowners to compare quotes through our online custom accounts we
          create for homeowners.
        </p>
        <H4>Contract Signing</H4>
        <p>
          Once you have determined which company you&apos;re going to go with
          and which panel design you prefer, you will enter a process called
          signing. This is simply letting your sales representative know or
          selecting online the proposal that you prefer and agreeing to move
          forward.
        </p>
        <p>
          If the Solar Company is an in-person business then they may have a
          representative sit in your living room and walk you through the
          proposal and have you sign the contract right there. These are often
          slightly high-pressure sales where either a customer is going to love
          their sales representative and be happy to sign or perhaps the sales
          representative sitting in the home is too aggressive and in the end
          alienates the customer and gets kicked out.
        </p>
        <p>
          Other solar companies will provide information virtually like
          EnergyPal and allow a customer to review the proposals and quotes in
          the comfort of their own home and answer questions by phone or email.
          These customers may sign right after they&apos;re presented the
          proposal or may come around a few months later when they&apos;re ready
          to sign the contracts.
        </p>
        <p>
          Signing the contract is a very straightforward process. Contracts and
          agreements are generated and shared online for you to review.
        </p>
        <H4>Site Survey</H4>
        <p>
          After signing the contract a series of processes are kicked off. The
          most apparent to you will be the scheduling of a site survey. This is
          when a person assesses the structural integrity of your home as well
          as the roof or ground. They will also look at sun exposure and inspect
          the electrical systems suitability for solar.
        </p>
        <p>
          You usually don&apos;t even need to be at home for the site survey to
          take place. In some cases the site surveyor will need to enter the
          home to look at the attic crawl space. This could be to plan a path
          for electrical conduit through the Attic or to double-check the
          structural Integrity of some roof Rafters or trusses. For you, a site
          survey is quick, easy, and painless. Sometimes the surveyors will even
          use drones to take photos and measurements of the roof.
        </p>
        <H4>Engineering</H4>
        <p>
          Also behind the scenes is the engineering process that occurs after
          you sign your contract. The engineering team is ready to take all the
          inputs from the initial solar design from the site survey results and
          from any other information that may have been provided by the customer
          in order to design the final layout and electrical configuration for
          the solar system. They will also do a structural review of the home to
          make sure the roof can safely support solar. The purpose of the
          engineering is to develop a set of plans that can be submitted to the
          Authority Having Jurisdiction (AHJ), which is a technical name for the
          city or town building permit office, to get the permits to do the
          project.
        </p>
        <p>
          From your perspective this is a step where you don&apos;t really have
          much to do unless the engineering department comes back with
          questions. Maybe the initial roof areas where panels would go is not
          going to work for structural issues or the property needs an upgrade
          to the main electrical panel. These questions and concerns will be
          relayed to you through your sales representative.
        </p>
        <Image src={Image3} alt="Engineering requirements for solar" />
        <H4>Permitting</H4>
        <p>
          Permitting is another step that is really a non-step for you. The
          solar company will take the engineering plans and signed documents
          from the customer to then submit to the local authority having
          Jurisdiction (AHJ).
        </p>
        <p>
          Within EnergyPal&apos;s current top service territories, installers
          manage over 3000 AHJs (cities, towns and counties) building permit
          offices. Because of the massive fragmentation of AHJs and the fact
          that many have their own particular rules and processes for obtaining
          permits, this can be an area of delay and frustration for homeowners
          and solar companies alike!
        </p>
        <p>
          <Link href="/solar-faq/do-i-need-permits-for-solar-panel-installation">
            Solar permits
          </Link>{' '}
          tend to take longer as building departments need to receive fire
          department approval or utility pre-approval before granting the
          building permit.
        </p>
        <Image src={Image4} alt="Technician installing solar panels." />
        <H4>Installation</H4>
        <p>
          Solar installation is an exciting time for you because you will
          actually see crews onsite and have visible progress on your solar
          system.
          <strong>
            Solar installations are usually 1 to 2 days depending on when the
            crew arrives.
          </strong>
        </p>
        <p>
          Typically a crew of 3-7 members will perform the installation. This
          consists of installing mounts into the roof material, laying “rails”
          or racking for the solar panel arrays, connecting the panels to the
          rails / racking, and wiring all of the solar panel arrays and
          inverter(s) to the main electrical panel.
        </p>
        <H4>System Activation</H4>
        <p>
          Once the system is installed you might be extremely excited but
          slightly disappointed because your system will not yet be functional.
          In order to become functional the solar company will need to request a
          final inspection and submit documentation to the AHJ and utility.{' '}
        </p>
        <p>
          After final inspections by the city and the utility the system will be
          granted <strong>permission to operate (PTO)</strong> by the electric
          utility serving you. Typically this point will happen between 2 weeks
          to 2 months after the installation. The long delay is usually due to
          the utility bureaucratic process and slow processing timelines.
        </p>
        <p>
          As soon as PTO is granted the system can be turned on (typically by
          the Solar Company personnel) then immediately when the sun is shining
          the system will produce power and feed the home and or the grid. You
          will now be able to start using your system monitoring app to view the
          system producing power.
        </p>
      </CenteredContentSection>
      <SideCardContent titles={['The Difficulty of Installation']} />
      <SideCardCTA />
      <FullWidthGuideFormSection cid={'a4oqmxxt5un31ha0'} />

      <SolarGuideTable />
    </article>
  );
};

export default Page;
